<template>
  <div v-if="!loading && recommendations.length" class="mx-5">
    <hr v-if="isAddToBasketPopover" class="w-full border-t border-black-10" />
    <div
      v-if="title"
      :class="{
        'my-2': isAddToBasketPopover,
        'my-10': !isAddToBasketPopover && !showReducedRecosSpacing,
        'mb-5 mt-10': !isAddToBasketPopover && showReducedRecosSpacing,
      }"
    >
      <span
        :class="{
          'font-semibold text-lg': isAddToBasketPopover,
          'text-xl font-normal': isPdp && !isAddToBasketPopover,
          'text-xl font-bold': !isPdp && !isAddToBasketPopover,
        }"
      >
        {{ title }}
      </span>
    </div>
    <BasicSlider
      :items="recommendations"
      key-prop="id"
      :pixel-gap="0"
      :center-content="recommendations.length < 4 && centerContent"
      @slide-visible="onSlideVisible"
    >
      <template #item="{ item: product, index }">
        <ProductCard
          :id="product.id"
          :key="`product-recommendation-${product.id}`"
          :product="product"
          :siblings="getSiblingsColors(product) as ProductSibling[]"
          :colors="getProductAndSiblingsColors(product, 'colorRange')"
          :name="getName(product)"
          :link="toLocalePath(getProductDetailRoute(product))"
          :image="getProductImage(product.images, imageAssetType)"
          :disable-hover-image="true"
          :price="lowestPrice(product)"
          :show-add-to-cart="false"
          :show-add-to-wishlist="false"
          :show-available-colors="false"
          :hide-more-colors-label="true"
          :show-price-reduction="false"
          highlight-price-red
          class="px-1"
          @click:product="handleProductClick(product, index)"
        />
      </template>
    </BasicSlider>
  </div>
</template>

<script setup lang="ts">
import type { Product, ProductSibling } from '@scayle/storefront-nuxt'
import {
  getFirstAttributeValue,
  getProductAndSiblingsColors,
} from '@scayle/storefront-nuxt'
import { RECO_LIST_CONTEXT } from '#layers/base/constants/recommendations'
import type { OdoscopeRecommendationsToken } from '#layers/base/constants/recommendations'
import useOdoscopeRecommendations from '~/composables/useOdoscopeRecommendations'
import useCarouselListTracking from '#layers/base/composables/useCarouselListTracking'
import BasicSlider from '~/components/ui/basicSlider/BasicSlider.vue'
import useUiState from '~/composables/ui/useUiState'
import useExperiments from '~/composables/useExperiments'

const props = defineProps<{
  token: OdoscopeRecommendationsToken
  title: string
  categoryId?: number
  productId?: number
  isAddToBasketPopover?: boolean
  centerContent?: boolean
  fetchOnClient?: boolean
}>()

const { isPdp } = useUiState()
const { showReducedRecosSpacing } = useExperiments()

const { centerContent = true } = props
const { recommendations, fetchRecommendations, loading, goControl } =
  useOdoscopeRecommendations(props.token, props.categoryId, props.productId)

if (!props.fetchOnClient) {
  onServerPrefetch(() => fetchRecommendations())
}

onMounted(() => {
  void fetchRecommendations()
})

const emit = defineEmits(['click:recommendation'])

const { onSlideVisible, onSelect } = useCarouselListTracking({
  context: computed(() => RECO_LIST_CONTEXT[props.token]),
  total: () => recommendations.value.length,
  // Never track Odoscope control-group lists, for any token. The shared fetch
  // still returns products when `goControl` is set, so without this gate MAD
  // would emit view_item_list/select_item for control users that AGS excludes.
  shouldTrack: () => !goControl.value,
})

const handleProductClick = (product: Product, index: number) => {
  onSelect(product, index)
  emit('click:recommendation', product, index)
}

const { toLocalePath } = useRouteHelpers()
const imageAssetType = '_f'

const getName = (product: Product) =>
  getFirstAttributeValue(product.attributes, 'name')?.label || ''

const lowestPrice = (product: Product) => getLowestPrice(product.variants || [])
</script>
